import React, { useState } from 'react';
import { Mail, Award, Download, Moon, Sun, MapPin, CalendarDays } from 'lucide-react';
import { useDarkMode } from './DarkModeContext';

import { logEvent } from './analytics';
import { sendContact } from './contactSend';
import FeaturedWork from './FeaturedWork';
import { AboutCard, BeyondCard, ExperienceCard, TechCard, CertsCard, ConnectCard, ReelPhoto, cardClass } from './HomeSections';
import content from './content-home.json';

const { profile } = content;
const resumeUrl = profile.resume_url || '/ChristianUbaldo_Resume.pdf';

const Portfolio = () => {
  const { darkMode, toggleDarkMode } = useDarkMode();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Track when someone downloads your resume
  const handleResumeDownload = () => {
    logEvent('Resume', 'Download', 'CV Download');
  };

  // Track when someone clicks "Let's Work Together"
  const handleContactClick = () => {
    logEvent('Contact', 'Click', 'Contact Form Open');
  };

  // Track social link clicks
  const handleSocialClick = (platform) => {
    logEvent('Social', 'Click', platform);
  };

  const playReel = (how = 'Profile photo') => logEvent('Reel', 'Play', how);

  // Track email clicks
  const handleEmailClick = () => {
    logEvent('Contact', 'Click', 'Email Link');
  };

const skills = profile.tech_stack;


  const certifications = profile.certifications.filter((c) => c.featured);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = {
      from_name: e.target.name.value,
      from_email: e.target.email.value,
      subject: e.target.subject.value,
      message: e.target.message.value,
    };

    try {
      await sendContact(formData);   // email + /admin inbox

      setSubmitStatus('success');
      e.target.reset();

      logEvent('Contact', 'Submit', 'Contact Form Success');
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');

      logEvent('Contact', 'Error', 'Contact Form Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-white'}`}>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 lg:pt-16 pb-8">

    {/* Header - Responsive: Desktop (previous) + Mobile (compact) */}
    <div className="mb-4 sm:mb-6">

      {/* Desktop Layout - Hidden on Mobile */}
      <div className="hidden sm:block">
        <div className="flex flex-col sm:flex-row items-start sm:items-stretch gap-4 sm:gap-6">
          {/* Profile Image */}
          <ReelPhoto darkMode={darkMode} reel={profile.reel} onPlay={playReel} className="self-center sm:self-auto">
          <img
            src="/professional-pic-320.jpg"
            alt="Christian Ubaldo"
            width={320}
            height={320}
            fetchPriority="high"
            className="w-32 sm:w-40 rounded-2xl object-cover flex-shrink-0 self-center sm:self-auto transition-transform duration-300 hover:scale-105"
          />
          </ReelPhoto>
          
          {/* Info Section */}
          <div className="flex-1 min-w-0 flex flex-col justify-between w-full">
            <div>
              <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 mb-2 flex-wrap sm:flex-nowrap">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <h1 className={`text-2xl sm:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Christian Ubaldo
                  </h1>
                  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" role="img" aria-label="Verified">
                      <path d="M12.0 0.5L12.7 0.6L13.4 1.0L14.1 1.6L14.6 2.2L15.1 2.7L15.7 3.1L16.3 3.2L17.1 3.2L17.9 3.2L18.7 3.2L19.5 3.4L20.2 3.8L20.6 4.5L20.8 5.3L20.8 6.1L20.8 6.9L20.8 7.7L20.9 8.3L21.3 8.9L21.8 9.4L22.4 9.9L23.0 10.6L23.4 11.3L23.5 12.0L23.4 12.7L23.0 13.4L22.4 14.1L21.8 14.6L21.3 15.1L20.9 15.7L20.8 16.3L20.8 17.1L20.8 17.9L20.8 18.7L20.6 19.5L20.2 20.2L19.5 20.6L18.7 20.8L17.9 20.8L17.1 20.8L16.3 20.8L15.7 20.9L15.1 21.3L14.6 21.8L14.1 22.4L13.4 23.0L12.7 23.4L12.0 23.5L11.3 23.4L10.6 23.0L9.9 22.4L9.4 21.8L8.9 21.3L8.3 20.9L7.7 20.8L6.9 20.8L6.1 20.8L5.3 20.8L4.5 20.6L3.8 20.2L3.4 19.5L3.2 18.7L3.2 17.9L3.2 17.1L3.2 16.3L3.1 15.7L2.7 15.1L2.2 14.6L1.6 14.1L1.0 13.4L0.6 12.7L0.5 12.0L0.6 11.3L1.0 10.6L1.6 9.9L2.2 9.4L2.7 8.9L3.1 8.3L3.2 7.7L3.2 6.9L3.2 6.1L3.2 5.3L3.4 4.5L3.8 3.8L4.5 3.4L5.3 3.2L6.1 3.2L6.9 3.2L7.7 3.2L8.3 3.1L8.9 2.7L9.4 2.2L9.9 1.6L10.6 1.0L11.3 0.6Z" fill="#0095F6"/>
                      <path d="M7.6 12.3l3 3 5.8-6.1" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    darkMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  aria-label="Toggle dark mode"
                >
                  <div className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-transform duration-300 flex items-center justify-center ${
                    darkMode ? 'translate-x-7 bg-white' : 'translate-x-0 bg-white'
                  }`}>
                    {darkMode ? <Moon size={14} className="text-blue-600" /> : <Sun size={14} className="text-yellow-500" />}
                  </div>
                </button>
              </div>
              
              <div className={`flex items-center gap-2 mb-1 text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <MapPin size={14} className="flex-shrink-0" />
                <span>{profile.location}</span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {profile.headline}
                </p>
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm bg-blue-600 text-white w-fit">
                  <Award size={16} className="flex-shrink-0" />
                  <span className="font-semibold">{profile.badge}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons - Desktop */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <a
                href={resumeUrl}
                download
                onClick={handleResumeDownload}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap flex-grow hover:shadow-lg transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                <Download size={14} className="flex-shrink-0" />
                <span className="truncate">Download Resume</span>
              </a>
              <button
                onClick={() => {
                    handleContactClick();
                    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-medium border transition-all duration-300 whitespace-nowrap flex-grow hover:shadow-md transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'border-gray-800 text-gray-300 hover:bg-[#1a1a1a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="truncate">Let's Work Together</span>
              </button>
              <a
                href={`mailto:${profile.email}`}
                onClick={handleEmailClick}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-medium border transition-all duration-300 whitespace-nowrap flex-grow hover:shadow-md transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'border-gray-800 text-gray-300 hover:bg-[#1a1a1a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Mail size={14} className="flex-shrink-0" />
                <span className="truncate">Send Email</span>
              </a>
              <a
                href="/community"
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-medium border transition-all duration-300 xs:col-span-2 sm:col-span-2 lg:col-span-1 flex-grow hover:shadow-md transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'border-gray-800 text-gray-300 hover:bg-[#1a1a1a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
                <span className="truncate">Join the Discussion Here!</span>
                <svg className="w-4 h-4 flex-shrink-0 hidden sm:block" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H2a1 1 0 110-2h12.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Compact Layout - Hidden on Desktop */}
      <div className="block sm:hidden">
        {/* Top Row: Image + Name + Toggle */}
        <div className="flex items-start gap-3 mb-3">
              <ReelPhoto darkMode={darkMode} reel={profile.reel} onPlay={playReel} className="mb-2 !rounded-xl">
              <img
                src="/professional-pic-320.jpg"
                alt="Christian Ubaldo"
                width={320}
                height={320}
                fetchPriority="high"
                className={`w-24 sm:w-28 h-24 sm:h-28 ${profile.reel?.video ? '' : 'mb-2 '}rounded-xl object-cover flex-shrink-0 transition-transform duration-300 hover:scale-105`}
              />
              </ReelPhoto>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className={`text-lg sm:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Christian Ubaldo
                    </p>
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" role="img" aria-label="Verified">
                      <path d="M12.0 0.5L12.7 0.6L13.4 1.0L14.1 1.6L14.6 2.2L15.1 2.7L15.7 3.1L16.3 3.2L17.1 3.2L17.9 3.2L18.7 3.2L19.5 3.4L20.2 3.8L20.6 4.5L20.8 5.3L20.8 6.1L20.8 6.9L20.8 7.7L20.9 8.3L21.3 8.9L21.8 9.4L22.4 9.9L23.0 10.6L23.4 11.3L23.5 12.0L23.4 12.7L23.0 13.4L22.4 14.1L21.8 14.6L21.3 15.1L20.9 15.7L20.8 16.3L20.8 17.1L20.8 17.9L20.8 18.7L20.6 19.5L20.2 20.2L19.5 20.6L18.7 20.8L17.9 20.8L17.1 20.8L16.3 20.8L15.7 20.9L15.1 21.3L14.6 21.8L14.1 22.4L13.4 23.0L12.7 23.4L12.0 23.5L11.3 23.4L10.6 23.0L9.9 22.4L9.4 21.8L8.9 21.3L8.3 20.9L7.7 20.8L6.9 20.8L6.1 20.8L5.3 20.8L4.5 20.6L3.8 20.2L3.4 19.5L3.2 18.7L3.2 17.9L3.2 17.1L3.2 16.3L3.1 15.7L2.7 15.1L2.2 14.6L1.6 14.1L1.0 13.4L0.6 12.7L0.5 12.0L0.6 11.3L1.0 10.6L1.6 9.9L2.2 9.4L2.7 8.9L3.1 8.3L3.2 7.7L3.2 6.9L3.2 6.1L3.2 5.3L3.4 4.5L3.8 3.8L4.5 3.4L5.3 3.2L6.1 3.2L6.9 3.2L7.7 3.2L8.3 3.1L8.9 2.7L9.4 2.2L9.9 1.6L10.6 1.0L11.3 0.6Z" fill="#0095F6"/>
                      <path d="M7.6 12.3l3 3 5.8-6.1" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  
                  <button
                    onClick={toggleDarkMode}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      darkMode ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    aria-label="Toggle dark mode"
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform flex items-center justify-center bg-white ${
                      darkMode ? 'translate-x-6' : 'translate-x-0'
                    }`}>
                      {darkMode ? <Moon size={12} className="text-blue-600" /> : <Sun size={12} className="text-yellow-500" />}
                    </div>
                  </button>
                </div>
                
                <div className={`flex items-center gap-1.5 text-[0.8125rem] mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <MapPin size={13} className="flex-shrink-0" />
                  <span className="truncate">{profile.location}</span>
                </div>

                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {profile.headline}
                </p>

                {/* Badge */}
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg !text-[0.6875rem] min-[375px]:!text-[0.71875rem] bg-blue-600 text-white w-fit max-w-full">
                  <Award size={10} className="flex-shrink-0 sm:w-3 sm:h-3" />
                  <span className="font-medium whitespace-nowrap">Civil Service Professional & Cum Laude</span>
                </div>
              </div>
            </div>

            {/* Action Buttons - Mobile Grid */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a
                href={resumeUrl}
                download
                onClick={handleResumeDownload}
                className={`inline-flex items-center justify-center tap-compact gap-2 h-10 px-3 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-lg transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                <Download size={14} />
                <span>Resume</span>
              </a>
              <button
                onClick={() => {
                handleContactClick();
                document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
                className={`inline-flex items-center justify-center tap-compact gap-2 h-10 px-3 rounded-lg text-sm font-medium border transition-all duration-300 hover:shadow-md transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'border-gray-800 text-gray-300 hover:bg-[#1a1a1a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>Hire Me</span>
              </button>
              <a
                href={`mailto:${profile.email}`}
                onClick={handleEmailClick}
                className={`inline-flex items-center justify-center tap-compact gap-2 h-10 px-3 rounded-lg text-sm font-medium border transition-all duration-300 hover:shadow-md transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'border-gray-800 text-gray-300 hover:bg-[#1a1a1a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Mail size={14} />
                <span>Email</span>
              </a>
              <a
                href="/community"
                className={`inline-flex items-center justify-center tap-compact gap-2 h-10 px-3 rounded-lg text-sm font-medium border transition-all duration-300 hover:shadow-md transform-gpu will-change-transform hover:-translate-y-0.5 ${
                  darkMode ? 'border-gray-800 text-gray-300 hover:bg-[#1a1a1a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
                <span>Discussion</span>
              </a>
            </div>
          </div>
        </div>

        {/* One responsive layout for every screen size (see HomeSections.js) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left: About, Tech Stack, Beyond Coding. Right: Experience (scrolls on desktop to match the left column).
              Phones: About, Experience, Tech Stack, Beyond Coding. */}
          <div className="contents lg:flex lg:flex-col lg:gap-6 lg:col-span-2">
            <AboutCard darkMode={darkMode} profile={profile} className="order-1 lg:order-none" />
            <TechCard darkMode={darkMode} skills={skills} className="order-3 lg:order-none" />
            <BeyondCard darkMode={darkMode} profile={profile} className="order-4 lg:order-none" />
          </div>
          <ExperienceCard darkMode={darkMode} roles={content.roles} className="order-2 lg:order-none" />
          <FeaturedWork darkMode={darkMode} sectionClass={cardClass(darkMode, 'order-5 lg:order-none lg:col-span-3')} />
          <CertsCard darkMode={darkMode} certifications={certifications} className="order-6 lg:order-none lg:col-span-2" />
          <ConnectCard darkMode={darkMode} profile={profile} onSocial={handleSocialClick} className="order-7 lg:order-none" />
        </div>

        {/* Contact Me Form - Full Width */}
        <section id="contact-form" className={`mt-4 sm:mt-6 p-4 sm:p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg ${darkMode ? 'bg-[#111] border-gray-800 hover:border-gray-700' : 'bg-white border-gray-300 hover:border-gray-400'}`}>
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <svg className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h2 className={`text-lg leading-tight font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact Me</h2>
        </div>

        {/* For people ready to decide who'd rather talk than write (link: /admin > Links > Book a call link) */}
        {profile.links?.booking && (
          <div className={`mb-3 sm:mb-4 p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between ${
            darkMode ? 'border-gray-800 bg-black' : 'border-gray-200 bg-gray-50'}`}>
            <div>
              <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Prefer to talk?</p>
              <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Book a 30-minute call, or send a message below.</p>
            </div>
            <a href={profile.links.booking} target="_blank" rel="noopener noreferrer"
              onClick={() => logEvent('Social', 'Click', 'Book a call')}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                darkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-black'}`}>
              <CalendarDays className="w-4 h-4" aria-hidden="true" /> Book a 30-minute call
            </a>
          </div>
        )}
        
        {/* ADD onSubmit HERE */}
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
                <label htmlFor="name" className={`block text-xs sm:text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Name *
                </label>
                <input
                type="text"
                id="name"
                name="name" 
                required
                className={`w-full px-3 sm:px-4 py-2 rounded-lg border text-xs sm:text-sm transition-colors ${
                    darkMode 
                    ? 'bg-black border-gray-800 text-white placeholder-gray-600 focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                placeholder="Your name"
                />
            </div>
            <div>
                <label htmlFor="email" className={`block text-xs sm:text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Email *
                </label>
                <input
                type="email"
                id="email"
                name="email" 
                required
                className={`w-full px-3 sm:px-4 py-2 rounded-lg border text-xs sm:text-sm transition-colors ${
                    darkMode 
                    ? 'bg-black border-gray-800 text-white placeholder-gray-600 focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                placeholder="your.email@example.com"
                />
            </div>
            </div>
            <div>
            <label htmlFor="subject" className={`block text-xs sm:text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Subject *
            </label>
            <input
                type="text"
                id="subject"
                name="subject" 
                required
                className={`w-full px-3 sm:px-4 py-2 rounded-lg border text-xs sm:text-sm transition-colors ${
                darkMode 
                    ? 'bg-black border-gray-800 text-white placeholder-gray-600 focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                placeholder="What is this about?"
            />
            </div>
            <div>
            <label htmlFor="message" className={`block text-xs sm:text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Message *
            </label>
            <textarea
                id="message"
                name="message"
                required
                rows={5}
                className={`w-full px-3 sm:px-4 py-2 rounded-lg border text-xs sm:text-sm transition-colors resize-none ${
                darkMode 
                    ? 'bg-black border-gray-800 text-white placeholder-gray-600 focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                placeholder="Your message..."
            />
            </div>

           {/* SUCCESS MESSAGE - Manual Close + Portfolio Colors */}
            {submitStatus === 'success' && (
            <div className={`p-4 rounded-xl border transition-all duration-300 animate-fadeIn mb-4 ${
                darkMode 
                ? 'bg-white/5 border-gray-700 text-white' 
                : 'bg-gray-50 border-gray-200 text-gray-900'
            }`}>
                <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    darkMode ? 'bg-white/10' : 'bg-gray-900/10'
                }`}>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1">
                    <p className={`font-bold text-sm mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Message Sent Successfully!
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Thanks for reaching out! I'll get back to you within 24 hours.
                    </p>
                </div>
                <button
                    onClick={() => setSubmitStatus(null)}
                    className={`flex-shrink-0 p-1 rounded-lg transition-colors ${
                    darkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-white/10' 
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    aria-label="Close"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                </div>
            </div>
            )}

            {/* ERROR MESSAGE - Manual Close + Portfolio Colors */}
            {submitStatus === 'error' && (
            <div className={`p-4 rounded-xl border transition-all duration-300 animate-fadeIn mb-4 ${
                darkMode 
                ? 'bg-white/5 border-gray-700 text-white' 
                : 'bg-gray-50 border-gray-200 text-gray-900'
            }`}>
                <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    darkMode ? 'bg-white/10' : 'bg-gray-900/10'
                }`}>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1">
                    <p className={`font-bold text-sm mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Oops! Something went wrong
                    </p>
                    <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Unable to send your message. Please try again or contact me directly.
                    </p>
                    <a 
                    href={`mailto:${profile.email}`} 
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                        darkMode 
                        ? 'bg-white/10 text-white hover:bg-white/20' 
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                    >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email me directly
                    </a>
                </div>
                <button
                    onClick={() => setSubmitStatus(null)}
                    className={`flex-shrink-0 p-1 rounded-lg transition-colors ${
                    darkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-white/10' 
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    aria-label="Close"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                </div>
            </div>
            )}
            
            {/* UPDATE BUTTON */}
            <button
            type="submit"
            disabled={isSubmitting} 
            className={`inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 w-full sm:w-auto hover:shadow-lg transform-gpu will-change-transform hover:-translate-y-0.5 ${
                darkMode ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-gray-900 text-white hover:bg-gray-800'
            } disabled:opacity-50 disabled:cursor-not-allowed`} 
            >
            <Mail size={16} className="flex-shrink-0" />
            {isSubmitting ? 'Sending...' : 'Send Message'} 
            </button>
        </form>
        </section>

        {/* Footer */}
        <footer className={`mt-12 pt-6 sm:pt-8 border-t ${darkMode ? 'border-gray-800' : 'border-gray-300'}`}>
            <div className="text-center">
                <p className={`font-semibold text-sm sm:text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                    © 2026 Christian Ubaldo. All rights reserved.
                </p>
            </div>
        </footer>
    </div>
    </div>
    );
};

export default Portfolio;